import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  ApplyOwnerReimbursementInput,
  ApplyOwnerReimbursementResult,
  ReclassifyPayrollTipsInput,
  ReclassifyPayrollTipsResult,
  CreatePayrollRunInput,
  ExtractPaystubInput,
  OpenPayrollCheck,
  PayrollExtractionResult,
  PayrollMapping,
  PayrollReimbursableEmployee,
  PayrollRun,
  PayrollRunWithDetails,
  UpdatePayrollMappingsInput,
  UpdateReimbursableEmployeesInput,
} from '@accounting/shared';
import { api } from '../lib/api';

/** Paycheques whose net pay has not yet cleared the bank, by person. */
export function useUncashedChecks(companyId: string | null) {
  return useQuery({
    queryKey: ['payroll-uncashed', companyId ?? 'none'],
    enabled: companyId !== null,
    queryFn: () =>
      api
        .get<{ checks: OpenPayrollCheck[] }>(`/companies/${companyId}/payroll/uncashed-checks`)
        .then((r) => r.checks),
  });
}

export function usePayrollRuns(companyId: string | null) {
  return useQuery({
    queryKey: ['payroll-runs', companyId ?? 'none'],
    enabled: companyId !== null,
    queryFn: () =>
      api.get<{ runs: PayrollRun[] }>(`/companies/${companyId}/payroll/runs`).then((r) => r.runs),
  });
}

export function usePayrollRun(companyId: string | null, id: string) {
  return useQuery({
    queryKey: ['payroll-run', companyId ?? 'none', id],
    enabled: companyId !== null && !!id,
    queryFn: () =>
      api
        .get<{ run: PayrollRunWithDetails }>(`/companies/${companyId}/payroll/runs/${id}`)
        .then((r) => r.run),
  });
}

export function usePayrollMappings(companyId: string | null) {
  return useQuery({
    queryKey: ['payroll-mappings', companyId ?? 'none'],
    enabled: companyId !== null,
    queryFn: () =>
      api
        .get<{ mappings: PayrollMapping[] }>(`/companies/${companyId}/payroll/mappings`)
        .then((r) => r.mappings),
  });
}

function invalidate(qc: ReturnType<typeof useQueryClient>, companyId: string | null) {
  const c = companyId ?? 'none';
  qc.invalidateQueries({ queryKey: ['payroll-runs', c] });
  qc.invalidateQueries({ queryKey: ['payroll-run', c] });
  qc.invalidateQueries({ queryKey: ['journal-entries', c] });
  qc.invalidateQueries({ queryKey: ['accounts-balances', c] });
  qc.invalidateQueries({ queryKey: ['report'] });
  qc.invalidateQueries({ queryKey: ['command-center'] });
}

export function useCreatePayrollRun(companyId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreatePayrollRunInput) => {
      if (!companyId) throw new Error('No active company');
      return api
        .post<{ run: PayrollRunWithDetails }>(`/companies/${companyId}/payroll/runs`, input)
        .then((r) => r.run);
    },
    onSuccess: () => invalidate(qc, companyId),
  });
}

export function useUpdatePayrollRun(companyId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: CreatePayrollRunInput }) => {
      if (!companyId) throw new Error('No active company');
      return api
        .patch<{ run: PayrollRunWithDetails }>(`/companies/${companyId}/payroll/runs/${id}`, input)
        .then((r) => r.run);
    },
    onSuccess: () => invalidate(qc, companyId),
  });
}

export function useVoidPayrollRun(companyId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => {
      if (!companyId) throw new Error('No active company');
      return api
        .post<{ run: PayrollRunWithDetails }>(`/companies/${companyId}/payroll/runs/${id}/void`, {
          reason,
        })
        .then((r) => r.run);
    },
    onSuccess: () => invalidate(qc, companyId),
  });
}

/**
 * AI payroll-PDF extraction (§17.12). Accepts a single-employee paystub OR a
 * multi-employee payroll register; in both cases returns PayrollExtractionResult
 * with a paystubs[] array (length 1 for a single paystub, length N for a
 * register, plus optional registerTotals). The mutation never writes anything —
 * the result is for the UI to pre-fill the new-run form. The user reviews,
 * edits if needed, and submits via useCreatePayrollRun as usual.
 */
export function useExtractPaystub(companyId: string | null) {
  return useMutation({
    mutationFn: (input: ExtractPaystubInput): Promise<PayrollExtractionResult> => {
      if (!companyId) throw new Error('No active company');
      return api.post<PayrollExtractionResult>(
        `/companies/${companyId}/payroll/extract-paystub`,
        input,
      );
    },
  });
}

export function useUpdatePayrollMappings(companyId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdatePayrollMappingsInput) => {
      if (!companyId) throw new Error('No active company');
      return api
        .put<{ mappings: PayrollMapping[] }>(`/companies/${companyId}/payroll/mappings`, input)
        .then((r) => r.mappings);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['payroll-mappings', companyId ?? 'none'] });
    },
  });
}

/**
 * Card tips breakout. Dry run by default; `dryRun: false` applies per stub
 * and reports applied, skipped and failed with reasons.
 */
export function useReclassifyPayrollTips(companyId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ReclassifyPayrollTipsInput) => {
      if (!companyId) throw new Error('No active company');
      return api
        .post<{ result: ReclassifyPayrollTipsResult }>(
          `/companies/${companyId}/payroll/reclassify-tips`,
          input,
        )
        .then((r) => r.result);
    },
    onSuccess: (res) => {
      if (!res.dryRun) invalidate(qc, companyId);
    },
  });
}

/** Employees whose payroll cost the owner pays back (DR Due from Owner / CR contra). */
export function useReimbursableEmployees(companyId: string | null) {
  return useQuery({
    queryKey: ['payroll-reimbursable', companyId ?? 'none'],
    enabled: companyId !== null,
    queryFn: () =>
      api
        .get<{ employees: PayrollReimbursableEmployee[] }>(
          `/companies/${companyId}/payroll/reimbursable-employees`,
        )
        .then((r) => r.employees),
  });
}

export function useUpdateReimbursableEmployees(companyId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateReimbursableEmployeesInput) => {
      if (!companyId) throw new Error('No active company');
      return api
        .put<{ employees: PayrollReimbursableEmployee[] }>(
          `/companies/${companyId}/payroll/reimbursable-employees`,
          input,
        )
        .then((r) => r.employees);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['payroll-reimbursable', companyId ?? 'none'] });
    },
  });
}

/**
 * Owner charge-back for stubs posted before their rule existed. Dry run by
 * default; `dryRun: false` rewrites each stub's entry and reports applied,
 * skipped and failed with reasons.
 */
export function useApplyOwnerReimbursement(companyId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ApplyOwnerReimbursementInput) => {
      if (!companyId) throw new Error('No active company');
      return api
        .post<{ result: ApplyOwnerReimbursementResult }>(
          `/companies/${companyId}/payroll/reimbursable-employees/apply`,
          input,
        )
        .then((r) => r.result);
    },
    onSuccess: (res) => {
      if (!res.dryRun) invalidate(qc, companyId);
    },
  });
}
